import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, MessageCircleQuestion, Send, CheckCircle2, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Modal Q&A para el postulante: enviar preguntas y ver respuestas del funcionario.
 * Solo se usa en postulaciones de convocatorias tipo "especial".
 *
 * Props:
 *   - postulacion: objeto postulacion (con id + convocatoria_nombre)
 *   - onClose: cerrar modal
 */
export default function PreguntasModal({ postulacion, onClose }) {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [pregunta, setPregunta] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_URL}/api/postulaciones/${postulacion.id}/preguntas`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('load');
      setItems(await res.json());
    } catch {
      toast.error('No se pudieron cargar las preguntas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const text = pregunta.trim();
    if (text.length < 3) {
      toast.error('La pregunta debe tener al menos 3 caracteres');
      return;
    }
    try {
      setSending(true);
      const res = await fetch(
        `${API_URL}/api/postulaciones/${postulacion.id}/preguntas`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ pregunta: text }),
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'send');
      }
      const nueva = await res.json();
      setItems((prev) => [...prev, nueva]);
      setPregunta('');
      toast.success('Pregunta enviada al funcionario');
    } catch (err) {
      toast.error(String(err.message || 'Error enviando pregunta'));
    } finally {
      setSending(false);
    }
  };

  const formatDate = (s) => {
    try {
      return new Date(s).toLocaleString('es-CO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return s;
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto"
      data-testid="preguntas-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full my-8 flex flex-col max-h-[85vh]">
        <div className="bg-slate-900 text-white rounded-t-xl p-5 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <MessageCircleQuestion className="h-6 w-6 text-amber-400 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold leading-tight">
                Preguntas sobre tu postulación
              </h3>
              <p className="text-xs text-slate-300 mt-1">
                {postulacion.convocatoria_nombre}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full w-8 h-8 flex items-center justify-center bg-slate-700 hover:bg-slate-600"
            aria-label="Cerrar"
            data-testid="preguntas-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-slate-50">
          {loading && (
            <p className="text-sm text-slate-500 text-center py-8">Cargando...</p>
          )}
          {!loading && items.length === 0 && (
            <div className="text-center py-8">
              <MessageCircleQuestion className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">
                Aún no has enviado preguntas. Escribe la primera abajo.
              </p>
            </div>
          )}
          {items.map((p) => (
            <div key={p.id} className="space-y-2" data-testid={`pregunta-${p.id}`}>
              {/* Pregunta bubble (own) */}
              <div className="flex justify-end">
                <div className="max-w-[85%] bg-sky-100 text-sky-950 rounded-2xl rounded-tr-md px-4 py-2.5 shadow-sm">
                  <p className="text-sm whitespace-pre-wrap">{p.pregunta}</p>
                  <div className="flex items-center gap-1 text-[10px] mt-1 text-sky-700 justify-end">
                    <Clock className="h-3 w-3" />
                    {formatDate(p.fecha_pregunta)}
                  </div>
                </div>
              </div>
              {/* Respuesta bubble (funcionario) */}
              {p.respuesta ? (
                <div className="flex justify-start">
                  <div className="max-w-[85%] bg-white border border-slate-200 rounded-2xl rounded-tl-md px-4 py-2.5 shadow-sm">
                    <div className="flex items-center gap-1 text-[10px] text-emerald-700 font-semibold mb-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Respuesta del funcionario
                    </div>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">
                      {p.respuesta}
                    </p>
                    <div className="text-[10px] text-slate-400 mt-1">
                      {formatDate(p.fecha_respuesta)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex justify-start">
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-3 py-1 text-xs font-medium">
                    Pendiente de respuesta
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <form
          onSubmit={send}
          className="p-4 border-t border-slate-200 bg-white rounded-b-xl"
        >
          <div className="flex gap-2">
            <textarea
              value={pregunta}
              onChange={(e) => setPregunta(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="Escribe tu pregunta al funcionario..."
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 resize-none"
              data-testid="pregunta-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(e);
              }}
            />
            <button
              type="submit"
              disabled={sending || pregunta.trim().length < 3}
              className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm font-medium"
              data-testid="pregunta-submit-btn"
            >
              <Send className="h-4 w-4" />
              {sending ? 'Enviando' : 'Enviar'}
            </button>
          </div>
          <div className="mt-1.5 flex justify-between text-[11px] text-slate-400">
            <span>Máx. 2000 caracteres · Ctrl+Enter para enviar</span>
            <span>{pregunta.length}/2000</span>
          </div>
        </form>
      </div>
    </div>
  );
}
